import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Container,
  Stack,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Inventory,
  LocationOn,
  CalendarToday,
  Warehouse,
  Storage,
  SpaceDashboard,
} from "@mui/icons-material";
import { racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvRackDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  const [loading, setLoading] = useState(true);
  const [rack, setRack] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRack();
  }, [id]);

  const fetchRack = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await racksAPI.getById(id);
      
      if (response.data && response.data.data && response.data.data.rack) {
        setRack(response.data.data.rack);
      } else {
        setError("Rack not found");
        enqueueSnackbar("Rack not found", { variant: "error" });
      }
    } catch (error) {
      console.error("Error fetching rack:", error);
      setError("Failed to load rack details");
      enqueueSnackbar("Error fetching rack details", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getZoneColor = (zone) => {
    const colors = {
      general: "primary",
      cold_storage: "info",
      controlled: "warning",
      hazardous: "error",
      quarantine: "secondary",
    };
    return colors[zone] || "default";
  };

  const formatZoneName = (zone) => {
    return zone
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return "error";
    if (utilization >= 75) return "warning";
    return "success";
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "default";
  };

  const getStatusVariant = (isActive) => {
    return isActive ? "filled" : "outlined";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Typography variant="h5" fontWeight="medium">
          Loading rack details...
        </Typography>
      </Box>
    );
  }

  if (error || !rack) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Warehouse sx={{ fontSize: 64, color: "error.main", mb: 2, opacity: 0.7 }} />
          <Typography variant="h4" color="error" gutterBottom fontWeight="bold">
            {error || "Rack not found"}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            We couldn't find the rack you're looking for
          </Typography>
          <Button 
            onClick={() => navigate("/hospital/hospital-inventory/racks")} 
            sx={{ mt: 2, px: 4, py: 1.5 }}
            variant="contained"
            size="large"
            startIcon={<ArrowBack />}
          >
            Back to Racks
          </Button>
        </Box>
      </Container>
    );
  }

  const utilization =
    rack.capacity > 0 ? (rack.currentOccupancy / rack.capacity) * 100 : 0;

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={() => navigate("/hospital/hospital-inventory/racks")}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2 }}
          >
            Back to Racks
          </Button>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {rack.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={rack.isActive ? "Active" : "Inactive"}
                color={getStatusColor(rack.isActive)}
                variant={getStatusVariant(rack.isActive)}
                size="medium"
              />
              <Chip
                icon={<Storage />}
                label={formatZoneName(rack.location.zone)}
                color={getZoneColor(rack.location.zone)}
                size="medium"
              />
            </Stack>
          </Box>
        </Stack>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            startIcon={<Edit />}
            onClick={() => navigate(`/hospital/hospital-inventory/racks/${id}/edit`)}
            variant="contained"
            size="large"
            sx={{ borderRadius: 2, px: 3 }}
            disabled={!rack.isActive}
          >
            Edit Rack
          </Button>
        )}
      </Box>

      {/* Status Alert for Inactive Racks */}
      {!rack.isActive && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, fontSize: '1.1rem', py: 1 }}>
          <Typography variant="body1" fontWeight="medium">
            This rack is currently inactive. No new stock can be added to inactive racks.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Details */}
        <Grid item xs={12} lg={8}>
          {/* Rack Information Card */}
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.75rem' }}>
                📋 Rack Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Rack Name
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.4rem' }}>
                    {rack.name}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={rack.isActive ? "Active" : "Inactive"}
                    color={getStatusColor(rack.isActive)}
                    variant={getStatusVariant(rack.isActive)}
                    size="large"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Storage Zone
                  </Typography>
                  <Chip
                    icon={<Storage />}
                    label={formatZoneName(rack.location.zone)}
                    color={getZoneColor(rack.location.zone)}
                    size="large"
                  />
                </Grid>

                {rack.description && (
                  <Grid item xs={12}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      Description
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.2rem', lineHeight: 1.6 }}>
                      {rack.description}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    📍 Location Details
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                      p: 2,
                      backgroundColor: 'background.default',
                      borderRadius: 2,
                    }}
                  >
                    <LocationOn fontSize="large" color="primary" />
                    <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 'medium' }}>
                      {[
                        rack.location.aisle,
                        rack.location.row,
                        rack.location.section,
                      ]
                        .filter(Boolean)
                        .join(" - ") || "Location not specified"}
                    </Typography>
                  </Box>
                </Grid>

                {rack.temperatureRange &&
                  (rack.temperatureRange.min || rack.temperatureRange.max) && (
                    <Grid item xs={12}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        🌡️ Temperature Range
                      </Typography>
                      <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 'medium' }}>
                        {rack.temperatureRange.min && rack.temperatureRange.max
                          ? `${rack.temperatureRange.min}°C - ${rack.temperatureRange.max}°C`
                          : rack.temperatureRange.min
                          ? `Min: ${rack.temperatureRange.min}°C`
                          : `Max: ${rack.temperatureRange.max}°C`}
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </CardContent>
          </Card>

          {/* Capacity & Utilization */}
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.75rem' }}>
                📊 Capacity & Utilization
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                    Current Occupancy: <strong>{rack.currentOccupancy}</strong> / <strong>{rack.capacity}</strong>
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color={getUtilizationColor(utilization)}>
                    {utilization.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(utilization, 100)}
                  color={getUtilizationColor(utilization)}
                  sx={{ 
                    height: 16, 
                    borderRadius: 3,
                    backgroundColor: 'background.default',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                    }
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Available: <strong>{rack.capacity - rack.currentOccupancy}</strong>
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Total Capacity: <strong>{rack.capacity}</strong>
                  </Typography>
                </Box>
              </Box>

              {utilization >= 90 && (
                <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '1.1rem' }}>
                  <Typography variant="body1" fontWeight="medium">
                    ⚠️ This rack is nearly full. Consider transferring some stock to other racks.
                  </Typography>
                </Alert>
              )}

              {!rack.isActive && rack.currentOccupancy > 0 && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: '1.1rem' }}>
                  <Typography variant="body1" fontWeight="medium">
                    ℹ️ This inactive rack contains {rack.currentOccupancy} items. 
                    Consider transferring stock to active racks.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Stock Items */}
          {rack.stockItems && rack.stockItems.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.75rem' }}>
                    📦 Current Stock ({rack.stockItems.length})
                  </Typography>
                  {!rack.isActive && (
                    <Chip 
                      label="Rack Inactive" 
                      color="warning" 
                      size="large"
                      icon={<Warehouse />}
                    />
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.light' }}>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rack.stockItems.map((stockItem, index) => (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{
                            opacity: !rack.isActive ? 0.7 : 1,
                            backgroundColor: !rack.isActive ? 'action.hover' : 'inherit',
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                              {stockItem.item?.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontSize: '1rem' }}
                            >
                              {stockItem.item?.genericName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontFamily: 'monospace' }}>
                              {stockItem.batchNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                              {stockItem.availableQuantity} / {stockItem.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CalendarToday fontSize="medium" color="action" />
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                                {new Date(stockItem.expiryDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={rack.isActive ? "Available" : "Inactive Rack"}
                              color={rack.isActive ? "success" : "default"}
                              size="medium"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {(!rack.stockItems || rack.stockItems.length === 0) && (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 6, textAlign: "center" }}>
                <Inventory
                  sx={{ fontSize: 80, color: "text.secondary", mb: 3, opacity: 0.5 }}
                />
                <Typography variant="h4" color="text.secondary" gutterBottom fontWeight="bold">
                  No Stock Items
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                  {rack.isActive 
                    ? "This rack is currently empty. Stock will appear here when items are assigned to this rack."
                    : "This rack is inactive and cannot hold new stock items."
                  }
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.5rem' }}>
                🚀 Quick Actions
              </Typography>

              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Inventory />}
                  size="large"
                  sx={{ py: 1.5, fontSize: '1.1rem', borderRadius: 2 }}
                  onClick={() => navigate("/inventory")}
                >
                  View Inventory
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LocationOn />}
                  size="large"
                  sx={{ py: 1.5, fontSize: '1.1rem', borderRadius: 2 }}
                  onClick={() => navigate("/racks")}
                >
                  View All Racks
                </Button>

                {hasAnyRole(["admin", "inventory_manager"]) && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Edit />}
                    size="large"
                    sx={{ py: 1.5, fontSize: '1.1rem', borderRadius: 2, mt: 1 }}
                    onClick={() => navigate(`/racks/${id}/edit`)}
                    disabled={!rack.isActive}
                  >
                    Edit Rack
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Rack Statistics */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.5rem' }}>
                📈 Rack Statistics
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={rack.isActive ? "Active" : "Inactive"}
                    color={getStatusColor(rack.isActive)}
                    variant={getStatusVariant(rack.isActive)}
                    size="large"
                  />
                </Box>

                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Utilization Rate
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={getUtilizationColor(utilization)}
                    sx={{ fontSize: '2.5rem' }}
                  >
                    {utilization.toFixed(1)}%
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Available Space
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpaceDashboard color="primary" />
                    <Typography variant="h3" fontWeight="bold" sx={{ fontSize: '2.2rem' }}>
                      {rack.capacity - rack.currentOccupancy}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Stock Items
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Inventory color="primary" />
                    <Typography variant="h3" fontWeight="bold" sx={{ fontSize: '2.2rem' }}>
                      {rack.stockItems?.length || 0}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Storage Zone
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.4rem' }}>
                    {formatZoneName(rack.location.zone)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
    </Box>
  );
};